import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Papa from "papaparse";
import {
  Box,
  Text,
  Table,
  Thead,
  Th,
  Tbody,
  Tr,
  Td,
  HStack,
  Badge,
  Flex,
  // IconButton,
} from "@chakra-ui/react";

import { chakraStyles, customStyles } from '../Form/Select/SelectStyles.jsx';
import { Select } from "chakra-react-select";
// import { AvailableIcons } from '../Icons/AvailableIcons.jsx';

import { fetchTransactionsByDateRange } from '../graphqlCompnents/Statement/fetchTransactionsByDateRange.jsx';
import { ButtonSecondaryPlain } from "../Form/Button/ButtonSecondaryPlain.jsx";
import { spellOutDate } from '../functions/dateTime.jsx';

// Helper function to group and sort transactions
const groupAndSortTransactions = (transactions) => {
  if (!transactions || !Array.isArray(transactions)) return {};

  const grouped = transactions.reduce((acc, transaction) => {
    const cardNo = transaction["Card No."];
    if (!acc[cardNo]) acc[cardNo] = [];
    acc[cardNo].push(transaction);
    return acc;
  }, {});

  Object.keys(grouped).forEach((cardNo) => {
    grouped[cardNo].sort((a, b) => {
      const dateA = a["Posted Date"] ? new Date(a["Posted Date"]) : new Date(0);
      const dateB = b["Posted Date"] ? new Date(b["Posted Date"]) : new Date(0);
      return dateA - dateB;
    });
  });

  return grouped;
};

// Helper function to calculate group totals
const calculateGroupTotals = (group) => {
  let debitTotal = 0;
  let creditTotal = 0;

  group.forEach((transaction) => {
    debitTotal += parseFloat(transaction.Debit || 0);
    creditTotal += parseFloat(transaction.Credit || 0);
  });

  const calculatedTotal = debitTotal - creditTotal;
  return { calculatedTotal };
};

// Transaction statuses written by the publish path. All four mean the same
// thing for reconciliation purposes: the transaction is paired and published.
// The suffix records the statement type, not a different state.
const PUBLISHED_STATUSES = new Set([
  "published",
  "published-invoice",
  "published-credit",
  "published-refund",
]);

// Helper function to determine transaction status
const getTransactionStatus = (transaction) => {
  if (PUBLISHED_STATUSES.has(transaction.status)) {
    return "Published";
  } else if (transaction.status === "pending_match" && transaction.matchStatus) {
    return "Ready for Approval";
  } else if (!transaction.status) {
    return "Pending Posted Transaction";
  } else if (!transaction.capitalOneCreditCardTransactionReceiptId) {
    return "Pending User Submission";
  } else {
    return "Unknown";
  }
};

// Helper function to get status color - ORIGINAL COLORS
const getStatusColor = (status) => {
  switch (status) {
    case 'Published':
      return { bg: '#333', color: 'white' };
    case 'Ready for Approval':
      return { bg: '#1c4a1f', color: 'white' };
    case 'Pending Posted Transaction':
      return { bg: '#660e00', color: 'white' };
    case 'Pending User Submission':
      return { bg: '#664600', color: 'white' };
    default:
      return { bg: 'purple', color: 'white' };
  }
};

export const StatementUploader = (props) => {
  const {
    mappedTeamMembers,
    statusFilter = 'all',
    onStatusFilterChange,
    statusOptions,
    sortField = '',
    sortDirection = 'asc',
    // onSort
  } = props;

  const [error, setError] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [transactionsByDateRange, setTransactionsByDateRange] = useState([]);
  const [transactionStartDate, setTransactionStartDate] = useState('');
  const [transactionEndDate, setTransactionEndDate] = useState('');
  const [processedData, setProcessedData] = useState(null);
  const [selectedCard, setSelectedCard] = useState({ value: "all", label: "Show All" });
  const [filteredProcessedData, setFilteredProcessedData] = useState(null);

  // Status options if not provided from parent
  const defaultStatusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'published', label: 'Published' },
    { value: 'ready_for_approval', label: 'Ready for Approval' },
    { value: 'pending_posted', label: 'Pending Posted Transaction' },
    { value: 'pending_submission', label: 'Pending User Submission' }
  ];

  const effectiveStatusOptions = statusOptions || defaultStatusOptions;

  useEffect(() => {
    const isValidDate = (dateStr) => {
      return !isNaN(new Date(dateStr).getTime());
    };

    const fetchTransactions = async () => {
      try {
        if (!isValidDate(transactionStartDate) || !isValidDate(transactionEndDate)) {
          throw new Error("Invalid date range provided.");
        }

        // The publish path writes a type-specific status to transaction.status
        // (see the publishedStatus assignment in ReceiptTransactionManagement):
        //   invoice -> published-invoice
        //   credit  -> published-credit
        //   refund  -> published-refund
        //   default -> published
        // Fetching only "published" left every refund, credit, and invoice out
        // of mergedTransactions entirely, so no key on either side could match
        // them and they rendered as "Pending Posted Transaction" even when they
        // were paired and published.
        //
        // Statuses NOT fetched here are deliberate: manually-published,
        // manually_published, published_manually, published-manually,
        // published_credit, manual_disconnect, sync_delete, manually_deleted
        // and deleted appear nowhere in application code. They were set by hand
        // to force a record out of the normal flow, and should stay out.
        const TRANSACTION_STATUSES = [
          "published",
          "published-invoice",
          "published-credit",
          "published-refund",
          "pending_match",
        ];

        const results = await Promise.all(
          TRANSACTION_STATUSES.map((status) =>
            fetchTransactionsByDateRange(
              status,
              transactionStartDate,
              transactionEndDate,
              1500
            )
          )
        );

        const mergedTransactions = results.flatMap((r) => r || []);

        setTransactionsByDateRange(mergedTransactions);
      } catch (error) {
        console.error("Error fetching transactions:", error);
        setError("Failed to fetch transactions. Please check the statement date range.");
      }
    };

    if (transactionStartDate && transactionEndDate) {
      fetchTransactions();
    }
  }, [transactionStartDate, transactionEndDate]);

  const normalizeAmount = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  const findMatchingTransactions = (uploadedTransactions, mergedTransactions) => {
    const mergedLookup = mergedTransactions.reduce((acc, transaction) => {
      const normalizedAmount = normalizeAmount(transaction.amount);
      const key = `${transaction.accountNumber}-${normalizedAmount}-${transaction.date}`;

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(transaction);
      return acc;
    }, {});

    return uploadedTransactions.map((transaction) => {
      // Capital One splits money-out and money-in into separate UNSIGNED columns
      // (Debit / Credit). The ERP stores a single SIGNED amount where positive
      // means money out, so a credit is stored negative. Reading the Credit
      // column as a positive number produced a key that could never match its
      // stored transaction, and the row fell through to
      // "Pending Posted Transaction" even when it was paired and published.
      // Negating the credit branch makes both sides agree.
      const debitRaw = transaction.Debit;
      const creditRaw = transaction.Credit;
      const hasDebit =
        debitRaw !== undefined && debitRaw !== null && String(debitRaw).trim() !== "";
      const normalizedAmount = hasDebit
        ? normalizeAmount(debitRaw)
        : normalizeAmount(-Math.abs(parseFloat(creditRaw || 0)));
      const key = `${transaction["Card No."].slice(-4)}-${normalizedAmount}-${transaction["Posted Date"]}`;

      const possibleMatches = mergedLookup[key] || [];
      const matchIndex = possibleMatches.findIndex((t) => !t.matched);
      let match = null;

      if (matchIndex !== -1) {
        match = possibleMatches[matchIndex];
        possibleMatches[matchIndex].matched = true;
      }

      return {
        ...transaction,
        isMatch: !!match,
        store: match ? match.store : null,
        status: match ? match.status : null,
        quickbooksExpenseId: match ? match.capitalOneCreditCardTransactionReceipt?.quickbooksExpenseId : null,
        transactionType: match ? match.capitalOneCreditCardTransactionReceipt?.transactionType : null,
        matchStatus: match ? match.capitalOneCreditCardTransactionReceipt?.matchStatus : null,
        displayStatus: getTransactionStatus({
          status: match ? match.status : null,
          matchStatus: match ? match.capitalOneCreditCardTransactionReceipt?.matchStatus : null,
          capitalOneCreditCardTransactionReceiptId: match ? match.capitalOneCreditCardTransactionReceiptId : null
        })
      };
    });
  };

  useEffect(() => {
    if (parsedData && transactionsByDateRange.length > 0 && mappedTeamMembers.length > 0) {
      const matchedData = findMatchingTransactions(parsedData, transactionsByDateRange);

      const updatedMatchedData = matchedData.map(transaction => {
        const cardNo = transaction["Card No."];
        
        if (!/^\d{4}$/.test(cardNo)) {
          return transaction;
        }

        const matchedMember = mappedTeamMembers.find(member => member.cardLastFour === cardNo);

        if (matchedMember?.teamMember) {
          const { firstName = "Unknown", lastName = "User", goesBy } = matchedMember?.teamMember || {};
          return {
            ...transaction,
            "Card No.": goesBy ? `${goesBy} ${lastName} ...${cardNo}` : `${firstName} ${lastName} ...${cardNo}`
          };
        }

        return transaction;
      });

      setProcessedData(updatedMatchedData);
    }
  }, [parsedData, transactionsByDateRange, mappedTeamMembers]);

  // Apply status filtering and sorting
  useEffect(() => {
    if (processedData) {
      let filtered = [...processedData];

      // Apply status filter
      if (statusFilter && statusFilter !== 'all') {
        filtered = filtered.filter(transaction => {
          const statusMap = {
            'published': 'Published',
            'ready_for_approval': 'Ready for Approval',
            'pending_posted': 'Pending Posted Transaction',
            'pending_submission': 'Pending User Submission'
          };
          return transaction.displayStatus === statusMap[statusFilter];
        });
      }

      // Apply card filter
      if (selectedCard.value !== "all") {
        filtered = filtered.filter(transaction => 
          transaction["Card No."] === selectedCard.value
        );
      }

      // Apply sorting
      if (sortField) {
        filtered.sort((a, b) => {
          let aValue, bValue;

          if (sortField === 'displayStatus') {
            const statusOrder = {
              'Published': 1,
              'Ready for Approval': 2,
              'Pending Posted Transaction': 3,
              'Pending User Submission': 4,
              'Unknown': 5
            };

            // Debug logging
            console.log('Sorting status:', a.displayStatus, b.displayStatus);
            console.log('Status order values:', statusOrder[a.displayStatus], statusOrder[b.displayStatus]);
            
            aValue = statusOrder[a.displayStatus] || 6;
            bValue = statusOrder[b.displayStatus] || 6;
          } else if (sortField === 'amount') {
            // Get the actual amount (Debit is positive, Credit is negative)
            aValue = parseFloat(a.Debit || -a.Credit || 0);
            bValue = parseFloat(b.Debit || -b.Credit || 0);
          } else if (sortField === 'date') {
            aValue = new Date(a["Posted Date"] || 0);
            bValue = new Date(b["Posted Date"] || 0);
          } else {
            aValue = a[sortField];
            bValue = b[sortField];
          }

          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }

      setFilteredProcessedData(filtered);
    }
  }, [processedData, statusFilter, selectedCard, sortField, sortDirection]);

  const handleFileUpload = (event) => {
    setError(null);
    const file = event.target.files[0];

    if (!file) {
      setError("No file selected.");
      return;
    }

    if (!file.type.includes("csv")) {
      setError("Please upload a valid CSV file.");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length) {
          setError("Failed to parse the CSV file. Please check the format.");
        } else {
          const data = results.data;

          const validPostedDates = data
            .map((row) => new Date(row["Posted Date"]))
            .filter((date) => !isNaN(date));

          if (validPostedDates.length === 0) {
            setError("No valid posted dates found in the file.");
            return;
          }

          const earliestDate = new Date(Math.min(...validPostedDates));
          const latestDate = new Date(Math.max(...validPostedDates));

          setParsedData(data);
          setTransactionStartDate(earliestDate.toISOString().split("T")[0]);
          setTransactionEndDate(latestDate.toISOString().split("T")[0]);
        }
      },
      error: (err) => {
        setError(`Error parsing CSV: ${err.message}`);
      },
    });
  };

  // const getSortIcon = (field) => {
  //   if (sortField !== field) return 'sort';
  //   return sortDirection === 'asc' ? 'sort-up' : 'sort-down';
  // };

  // const handleStatusSort = () => {
  //   onSort('displayStatus');
  // };

  // const handleAmountSort = () => {
  //   onSort('amount');
  // };

  // const handleDateSort = () => {
  //   onSort('Posted Date');
  // };

  // FIXED: Extract unique card numbers for the select dropdown
  const cardOptions = processedData
    ? [
        { value: "all", label: "Show All" },
        ...Array.from(new Set(processedData.map((t) => t["Card No."])))
          .filter(cardNo => cardNo && cardNo !== "all")
          .map(cardNo => ({ value: cardNo, label: cardNo }))
      ]
    : [{ value: "all", label: "Show All" }];

  return (
    <Box>
      {/* File Upload */}
      <label>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
        <HStack>
          <Box paddingRight={'25px'}>
            <ButtonSecondaryPlain 
              width="210px"
              type="button"
              name='uploadFile'
              value='Upload Billing Statement'
              onClick={() => document.querySelector("input[type=file]").click()}
            />
          </Box>
          {(transactionStartDate && transactionEndDate) && 
            <Box>
              <Text as="span" className="text-1">Billing statement date range: </Text>
              <Text as="span" className="label-1">{spellOutDate(transactionStartDate, 'apple-ny')} - {spellOutDate(transactionEndDate, 'apple-ny')}</Text>
            </Box>
          }
        </HStack>
      </label>

      {error && <Text color="red.500" mt={2}>{error}</Text>}

      {/* Filters Section */}
      {processedData && (
        <Flex gap={4} mt={4} flexWrap="wrap" alignItems="end">
          {/* Card Filter */}
          <Box minW="200px">
            <Text mb={2} className="dark-sfpro-text-2">Filter by Card No.</Text>
            <Select
              value={selectedCard}
              onChange={setSelectedCard}
              options={cardOptions}
              chakraStyles={chakraStyles}
              selectedOptionColorScheme="spikegray"
              className="select"
              styles={customStyles}
              placeholder="Filter by Card No."
            />
          </Box>

          {/* Status Filter */}
          <Box minW="200px">
            <Text mb={2} className="dark-sfpro-text-2">Filter by Status</Text>
            <Select
              value={effectiveStatusOptions.find(opt => opt.value === statusFilter)}
              onChange={(option) => onStatusFilterChange(option)}
              options={effectiveStatusOptions}
              chakraStyles={chakraStyles}
              selectedOptionColorScheme="spikegray"
              className="select"
              styles={customStyles}
              placeholder="Filter by Status"
            />
          </Box>

          {/* Summary */}
          <Box flex={1} textAlign="right">
            <Text className="dark-sfpro-text-2">
              Showing {filteredProcessedData ? filteredProcessedData.length : processedData.length} of {processedData.length} transactions
            </Text>
          </Box>
        </Flex>
      )}

      {/* Display grouped and sorted data as a table */}
      {parsedData && (
        <Box 
          mt={4} 
          border="1px" 
          borderColor="gray.200" 
          borderRadius="6px" 
          overflow="hidden">
          <Table variant="simple" size="md">
            <Thead>
              <Tr>
                <Th>
                  <HStack spacing={1}>
                    <Text>Status</Text>
                    {/* <IconButton
                      aria-label="Sort by status"
                      icon={<AvailableIcons boxSize="16px" iconRef={getSortIcon('displayStatus')} />}
                      size="xs"
                      variant="ghost"
                      onClick={handleStatusSort}
                    /> */}
                  </HStack>
                </Th>
                <Th>Trans Date</Th>
                <Th>
                  <HStack spacing={1}>
                    <Text>Post Date</Text>
                    {/* <IconButton
                      aria-label="Sort by date"
                      icon={<AvailableIcons boxSize="16px" iconRef={getSortIcon('date')} />}
                      size="xs"
                      variant="ghost"
                      onClick={handleDateSort}
                    /> */}
                  </HStack>
                </Th>
                <Th>Description</Th>
                <Th>
                  <HStack spacing={1}>
                    <Text>Amount</Text>
                    {/* <IconButton
                      aria-label="Sort by amount"
                      icon={<AvailableIcons boxSize="16px" iconRef={getSortIcon('amount')} />}
                      size="xs"
                      variant="ghost"
                      onClick={handleAmountSort}
                    /> */}
                  </HStack>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {Object.entries(groupAndSortTransactions(filteredProcessedData || processedData))
                .map(([cardNo, transactions]) => {
                  const { calculatedTotal } = calculateGroupTotals(transactions);
                  
                  return (
                    <React.Fragment key={cardNo}>
                      <Tr>
                        <Td colSpan="5">
                          <Text fontWeight="bold" fontSize="md">
                            {cardNo}: Transactions ({transactions.length})
                          </Text>
                        </Td>
                      </Tr>
                      {/* Render transactions for the card */}
                      {transactions.map((transaction, index) => {
                        const status = getTransactionStatus(transaction);
                        const statusColors = getStatusColor(status);
                        
                        return (
                          <Tr 
                            key={index} 
                            // _hover={{ bg: 'gray.50' }}
                            >
                            <Td>
                              <Badge 
                                variant="solid" 
                                borderRadius={'6px'} 
                                paddingX={'15px'} 
                                paddingY={'5px'} 
                                bg={statusColors.bg}
                                color={statusColors.color}
                                textAlign="center" 
                                display="inline-block" 
                                minWidth="200px" 
                                textTransform="none"
                                fontSize="sm"
                              >
                                {status}
                              </Badge>
                            </Td>
                            <Td>{spellOutDate(transaction["Transaction Date"], 'moy-d')}</Td>
                            <Td>{spellOutDate(transaction["Posted Date"], 'moy-d')}</Td>
                            <Td>
                              <Text fontSize="sm">{transaction.Description}</Text>
                              {transaction.store && (
                                <Text fontSize="xs">{transaction.store}</Text>
                              )}
                            </Td>
                            <Td>
                              {transaction.Debit ? (
                                <Text fontSize="sm">${transaction.Debit}</Text>
                              ) : transaction.Credit ? (
                                <Text fontSize="sm" color="green.500">-${transaction.Credit}</Text>
                              ) : (
                                <Text fontSize="sm" color="red.500">$0.00</Text>
                              )}
                            </Td>
                          </Tr>
                        );
                      })}

                      {/* Subtotal row */}
                      <Tr fontWeight="bold">
                        <Td colSpan="4">
                          <Text fontSize="sm">Subtotal for {cardNo}</Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">${calculatedTotal.toFixed(2)}</Text>
                        </Td>
                      </Tr>
                    </React.Fragment>
                  );
                })} 
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

// Updated PropTypes for validation
StatementUploader.propTypes = {
  mappedTeamMembers: PropTypes.array.isRequired,
  statusFilter: PropTypes.string,
  onStatusFilterChange: PropTypes.func,
  statusOptions: PropTypes.array,
  sortField: PropTypes.string,
  sortDirection: PropTypes.string,
  onSort: PropTypes.func,
};